import React, { useState, useEffect, useRef, useContext } from 'react';
import { createContext } from 'react';
import {
    Button, Box, Typography, Dialog, DialogTitle, DialogContent,
    DialogActions, DialogContentText
} from '@mui/material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
const TimerContext = createContext();

export const TimerProvider = ({ children }) => {
    const [countdownActive, setCountdownActive] = useState(false);
    const [countdownSeconds, setCountdownSeconds] = useState(300);
    const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
    const [currentTicketId, setCurrentTicketId] = useState(null);
    const [autoCloseCallback, setAutoCloseCallback] = useState(null);
    const [isClosing, setIsClosing] = useState(false); // Nueva bandera

    const timerRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    const closeConversation = async () => {
        if (isClosing || !autoCloseCallback) return;

        setIsClosing(true);
        try {
            await autoCloseCallback();
            setShowTimeoutDialog(true);
        } catch (error) {
            console.error('Error en cierre automático:', error);
        } finally {
            resetTimer();
            setIsClosing(false);
        }
    };

    // Limpieza de efectos
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, []);

    // Efecto principal del temporizador
    useEffect(() => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }

        if (countdownActive && countdownSeconds > 0) {
            // Temporizador principal para el cierre
            timerRef.current = setTimeout(() => {
                closeConversation();
            }, countdownSeconds * 1000);

            // Intervalo para actualizar el contador
            countdownIntervalRef.current = setInterval(() => {
                setCountdownSeconds(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownIntervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else if (countdownActive && countdownSeconds <= 0) {
            closeConversation();
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [countdownActive, countdownSeconds]);

    const startTimer = (ticketId, initialSeconds = 300, callback) => {
        stopTimer();
        setCurrentTicketId(ticketId);
        setCountdownSeconds(initialSeconds);
        setCountdownActive(true);
        setAutoCloseCallback(() => callback);
    };

    const stopTimer = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        setCountdownActive(false);
    };

    const resetTimer = () => {
        stopTimer();
        setCountdownSeconds(300);
        setCurrentTicketId(null);
        setAutoCloseCallback(null);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };    

    const handleNewUserMessage = (ticketId) => {
        console.log('Nuevo mensaje detectado para ticketId:', ticketId, 'currentTicketId:', currentTicketId);
    
        if (countdownActive && String(currentTicketId) === String(ticketId)) {
            console.log('Resetting timer due to new user message...');
            resetTimer();
        }
    };
    
    
    return (
        <TimerContext.Provider
            value={{
                countdownActive,
                countdownSeconds,
                startTimer,
                stopTimer,
                resetTimer,
                currentTicketId,
                handleNewUserMessage,
                setShowTimeoutDialog
            }}
        >
            {children}
            <Dialog
                open={showTimeoutDialog}
                onClose={() => setShowTimeoutDialog(false)}
                BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}
            >
                <DialogTitle style={{ backgroundColor: '#19438C', color: 'white' }}>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <AccessAlarmIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" component="span">
                            Tiempo agotado
                        </Typography>
                        <AccessAlarmIcon style={{ marginLeft: 8 }} />
                    </Box>
                </DialogTitle>
                <DialogContent style={{ padding: '20px' }}>
                    <DialogContentText textAlign={'justify'}>
                        Una de tus conversaciones asignadas en INJUPEMP Virtual se cerró automáticamente porque el tiempo de espera se agotó y no se recibió un nuevo mensaje.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <Button
                        onClick={() => setShowTimeoutDialog(false)}
                        variant="contained"
                        style={{ margin: '0 auto', backgroundColor: '#19438C' }}
                    >
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>
        </TimerContext.Provider>
    );
};

export const useTimer = () => useContext(TimerContext);
